/**
 * Unified Notification Platform — registry-driven kinds (Phase 4.1 / ADR-035).
 * Platform infrastructure — not an ADR-028 business aggregate.
 */

export const NOTIFICATION_CATEGORIES = Object.freeze({
  FRIENDS: "friends",
  ORGANIZATIONS: "organizations",
  SESSIONS: "sessions",
  ARENA: "arena",
  TOURNAMENT: "tournament",
  PLAYER: "player",
  SYSTEM: "system",
});

export const NOTIFICATION_CATEGORY_LABELS = Object.freeze({
  friends: "Friends",
  organizations: "Organizations",
  sessions: "Sessions",
  arena: "Arena",
  tournament: "Tournament",
  player: "Player",
  system: "System",
});

export const NOTIFICATION_PRIORITIES = Object.freeze({
  CRITICAL: "critical",
  HIGH: "high",
  NORMAL: "normal",
  LOW: "low",
});

export const NOTIFICATION_STATUSES = Object.freeze({
  UNREAD: "unread",
  READ: "read",
  DISMISSED: "dismissed",
  ARCHIVED: "archived",
});

export const NOTIFICATION_KINDS = Object.freeze({
  MATCH_READY: "match_ready",
  COURT_ASSIGNED: "court_assigned",
  MATCH_COMPLETE: "match_complete",
  TOURNAMENT_COMPLETE: "tournament_complete",
  REGISTRATION_SUBMITTED: "registration_submitted",
  REGISTRATION_CONFIRMED: "registration_confirmed",
  REGISTRATION_CLOSED: "registration_closed",
  TOURNAMENT_STARTED: "tournament_started",
  ARENA_INVITATION: "arena_invitation",
  FRIEND_REQUEST: "friend_request",
  FRIEND_ACCEPTED: "friend_accepted",
  ORG_INVITE: "org_invite",
  ORG_JOIN_REQUEST: "org_join_request",
  ORG_JOIN_APPROVED: "org_join_approved",
  ORG_ANNOUNCEMENT: "org_announcement",
  SESSION_CREATED: "session_created",
  SESSION_RSVP_DIGEST: "session_rsvp_digest",
  ACHIEVEMENT_EARNED: "achievement_earned",
  SYSTEM: "system",
});

export const NOTIFICATION_KIND_LABELS = Object.freeze({
  match_ready: "Match ready",
  court_assigned: "Court assignment",
  match_complete: "Match complete",
  tournament_complete: "Tournament complete",
  registration_submitted: "Registration submitted",
  registration_confirmed: "Registration confirmed",
  registration_closed: "Registration closed",
  tournament_started: "Tournament started",
  arena_invitation: "Arena invitation",
  friend_request: "Friend request",
  friend_accepted: "Friend accepted",
  org_invite: "Organization invite",
  org_join_request: "Join request",
  org_join_approved: "Join approved",
  org_announcement: "Announcement",
  session_created: "Session",
  session_rsvp_digest: "Session attendance",
  achievement_earned: "Achievement",
  system: "System",
});

/** @type {Readonly<Record<string, { category: string, priority: string, preferenceKey: string }>>} */
export const NOTIFICATION_REGISTRY = Object.freeze({
  [NOTIFICATION_KINDS.FRIEND_REQUEST]: {
    category: NOTIFICATION_CATEGORIES.FRIENDS,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "friends",
  },
  [NOTIFICATION_KINDS.FRIEND_ACCEPTED]: {
    category: NOTIFICATION_CATEGORIES.FRIENDS,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "friends",
  },
  [NOTIFICATION_KINDS.ARENA_INVITATION]: {
    category: NOTIFICATION_CATEGORIES.ARENA,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "arena",
  },
  [NOTIFICATION_KINDS.ORG_INVITE]: {
    category: NOTIFICATION_CATEGORIES.ORGANIZATIONS,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "organizations",
  },
  [NOTIFICATION_KINDS.ORG_JOIN_REQUEST]: {
    category: NOTIFICATION_CATEGORIES.ORGANIZATIONS,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "organizations",
  },
  [NOTIFICATION_KINDS.ORG_JOIN_APPROVED]: {
    category: NOTIFICATION_CATEGORIES.ORGANIZATIONS,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "organizations",
  },
  [NOTIFICATION_KINDS.ORG_ANNOUNCEMENT]: {
    category: NOTIFICATION_CATEGORIES.ORGANIZATIONS,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "organizations",
  },
  [NOTIFICATION_KINDS.SESSION_CREATED]: {
    category: NOTIFICATION_CATEGORIES.SESSIONS,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "sessions",
  },
  [NOTIFICATION_KINDS.SESSION_RSVP_DIGEST]: {
    category: NOTIFICATION_CATEGORIES.SESSIONS,
    priority: NOTIFICATION_PRIORITIES.LOW,
    preferenceKey: "sessions",
  },
  [NOTIFICATION_KINDS.MATCH_READY]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.CRITICAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.COURT_ASSIGNED]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.CRITICAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.MATCH_COMPLETE]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.TOURNAMENT_COMPLETE]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.REGISTRATION_SUBMITTED]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.REGISTRATION_CONFIRMED]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.REGISTRATION_CLOSED]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.TOURNAMENT_STARTED]: {
    category: NOTIFICATION_CATEGORIES.TOURNAMENT,
    priority: NOTIFICATION_PRIORITIES.HIGH,
    preferenceKey: "tournament",
  },
  [NOTIFICATION_KINDS.ACHIEVEMENT_EARNED]: {
    category: NOTIFICATION_CATEGORIES.PLAYER,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "player",
  },
  [NOTIFICATION_KINDS.SYSTEM]: {
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "system",
  },
});

export const DEFAULT_NOTIFICATION_PREFERENCES = Object.freeze({
  categories: Object.freeze({
    friends: true,
    organizations: true,
    sessions: true,
    arena: true,
    tournament: true,
    player: true,
    system: true,
  }),
  digest: Object.freeze({
    weekly: false,
  }),
});

export const MAX_NOTIFICATION_FANOUT = 50;
export const NOTIFICATION_INBOX_PAGE_SIZE = 40;
export const NOTIFICATION_GROUP_WINDOW_MS = 6 * 60 * 60 * 1000;

export function getNotificationRegistryEntry(kind) {
  return NOTIFICATION_REGISTRY[kind] || {
    category: NOTIFICATION_CATEGORIES.SYSTEM,
    priority: NOTIFICATION_PRIORITIES.NORMAL,
    preferenceKey: "system",
  };
}

export function isCategoryEnabled(prefs, kind) {
  const entry = getNotificationRegistryEntry(kind);
  const cats = prefs?.categories || DEFAULT_NOTIFICATION_PREFERENCES.categories;
  if (cats[entry.preferenceKey] === false) return false;
  return true;
}

export function mergeNotificationPreferences(raw) {
  const base = DEFAULT_NOTIFICATION_PREFERENCES;
  const cats = { ...base.categories, ...(raw?.categories || {}) };
  const digest = { ...base.digest, ...(raw?.digest || {}) };
  return { categories: cats, digest };
}

export function newNotificationId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return `unt_${crypto.randomUUID()}`;
  }
  return `unt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Resolve deep-link for a notification (registry-aware).
 * @param {object} n
 */
export function resolveNotificationHref(n) {
  if (!n) return "/activity";
  if (n.href) return n.href;
  if (n.kind === NOTIFICATION_KINDS.FRIEND_REQUEST || n.kind === NOTIFICATION_KINDS.FRIEND_ACCEPTED) {
    return "/friends?tab=pending";
  }
  if (n.kind === NOTIFICATION_KINDS.ARENA_INVITATION && n.meta?.invitationId) {
    return `/join/${encodeURIComponent(n.meta.invitationId)}`;
  }
  if (n.organizationId) {
    return `/organization/${encodeURIComponent(n.organizationId)}`;
  }
  if (n.competitionId) {
    return `/browse/${encodeURIComponent(n.competitionId)}`;
  }
  return "/activity";
}

export function isNotificationUnread(n) {
  if (!n) return false;
  if (n.status === NOTIFICATION_STATUSES.DISMISSED || n.status === NOTIFICATION_STATUSES.ARCHIVED) {
    return false;
  }
  if (n.status === NOTIFICATION_STATUSES.UNREAD) return true;
  if (n.status === NOTIFICATION_STATUSES.READ) return false;
  return n.read === false;
}

export function isNotificationVisible(n) {
  if (!n) return false;
  return (
    n.status !== NOTIFICATION_STATUSES.DISMISSED &&
    n.status !== NOTIFICATION_STATUSES.ARCHIVED
  );
}

/**
 * @param {string} kind
 * @param {string} [entityKey]
 */
export function buildDedupeKey(kind, entityKey) {
  if (!kind || !entityKey) return null;
  return `${kind}:${entityKey}`;
}

/**
 * Group visible notifications by calendar day for Center UI.
 * @param {object[]} items
 * @param {number} [nowMs]
 */
export function groupNotificationsByDate(items, nowMs = Date.now()) {
  const startOfToday = new Date(nowMs);
  startOfToday.setHours(0, 0, 0, 0);
  const todayMs = startOfToday.getTime();
  const yesterdayMs = todayMs - 86400000;

  /** @type {{ key: string, label: string, items: object[] }[]} */
  const buckets = [];
  const map = new Map();

  for (const n of items || []) {
    const ms = n.createdAtMs || 0;
    let key;
    let label;
    if (ms >= todayMs) {
      key = "today";
      label = "Today";
    } else if (ms >= yesterdayMs) {
      key = "yesterday";
      label = "Yesterday";
    } else {
      const d = new Date(ms);
      key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      label = d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: d.getFullYear() !== new Date(nowMs).getFullYear() ? "numeric" : undefined,
      });
    }
    if (!map.has(key)) {
      const bucket = { key, label, items: [] };
      map.set(key, bucket);
      buckets.push(bucket);
    }
    map.get(key).items.push(n);
  }
  return buckets;
}

/**
 * Create inbox document shell (refs only — no embedded aggregates).
 * @param {object} input
 */
export function createNotificationShell(input = {}) {
  const kind = input.kind || NOTIFICATION_KINDS.SYSTEM;
  const reg = getNotificationRegistryEntry(kind);
  const id = input.notificationId || newNotificationId();
  const status = input.status || NOTIFICATION_STATUSES.UNREAD;
  const createdAtMs = input.createdAtMs || Date.now();

  return {
    notificationId: id,
    userId: input.userId || null,
    kind,
    category: input.category || reg.category,
    priority: input.priority || reg.priority,
    title: input.title || NOTIFICATION_KIND_LABELS[kind] || "Notification",
    body: input.body || "",
    href: input.href || null,
    entityType: input.entityType || null,
    entityId: input.entityId || null,
    organizationId: input.organizationId || null,
    competitionId: input.competitionId || null,
    drawId: input.drawId || null,
    fixtureId: input.fixtureId || null,
    sessionId: input.sessionId || null,
    relationshipId: input.relationshipId || input.meta?.relationshipId || null,
    invitationId: input.invitationId || input.meta?.invitationId || null,
    groupKey: input.groupKey || null,
    groupCount: input.groupCount != null ? Number(input.groupCount) : 1,
    dedupeKey: input.dedupeKey || null,
    status,
    read: status !== NOTIFICATION_STATUSES.UNREAD,
    createdAtMs,
    readAtMs: input.readAtMs || null,
    dismissedAtMs: input.dismissedAtMs || null,
    expiresAtMs: input.expiresAtMs || null,
    channels: {
      inApp: true,
      push: false,
      email: false,
      sms: false,
      whatsapp: false,
      ...(input.channels || {}),
    },
    summaryEligible: input.summaryEligible !== false,
    meta: input.meta || {},
  };
}

/**
 * Paths where the platform bell must not render.
 * @param {string} pathname
 */
export function shouldHideNotificationBell(pathname) {
  const p = String(pathname || "");
  if (!p) return false;
  if (p.startsWith("/tv/")) return true;
  if (p.startsWith("/watch/")) return true;
  if (/\/session(\/|$)/.test(p) && p.includes("/room/")) return true;
  return false;
}

export function formatUnreadBadgeCount(count) {
  const n = Number(count) || 0;
  if (n <= 0) return "";
  if (n > 99) return "99+";
  return String(n);
}
